import { CheckCircle, Robot, UserCircle, Warning } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Dialog } from "~/foundation/components/Dialog";

export interface TelegramHelpStatus {
  configured: boolean;
  bot: string | null;
  linked: boolean;
  username: string | null;
}

/**
 * The Telegram help window (2026-10-17): what the channel does, where this server and this person
 * stand right now, how a person links their chat (every person, once), and how an administrator
 * creates the bot with @BotFather and installs its token (once per server). Every step names what
 * the product really does — the one-time 15-minute link, the private chat, the two commands, the
 * `.env` key and the two containers to redeploy — so nobody has to guess.
 */
export function TelegramHelp({
  open,
  onOpenChange,
  status,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status?: TelegramHelpStatus | null;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("Telegram — how it works")}
      description={t(
        "Docit sends you a Telegram message when a question waits for you or an important mail arrives, and you answer with a tap. Two things must be true: the bot exists on this server, and your chat is linked to it.",
      )}
      closeLabel={t("Close")}
      size="lg"
    >
      <div className="flex flex-col gap-5 text-body text-text">
        {status ? (
          <ul className="flex flex-col gap-1 rounded-md border border-divider bg-bg-secondary p-3 text-small">
            <li className="flex items-center gap-2">
              {status.configured ? (
                <CheckCircle size={16} className="text-[color:var(--success)]" aria-hidden />
              ) : (
                <Warning size={16} className="text-[color:var(--warning)]" aria-hidden />
              )}
              <Robot size={16} aria-hidden />
              <span>
                {status.configured
                  ? status.bot
                    ? t("The bot is set up on this server as @{{bot}}.", { bot: status.bot })
                    : t("The bot is set up on this server.")
                  : t(
                      "The bot is not set up on this server yet — see the administrator steps below.",
                    )}
              </span>
            </li>
            <li className="flex items-center gap-2">
              {status.linked ? (
                <CheckCircle size={16} className="text-[color:var(--success)]" aria-hidden />
              ) : (
                <Warning size={16} className="text-[color:var(--warning)]" aria-hidden />
              )}
              <UserCircle size={16} aria-hidden />
              <span>
                {status.linked
                  ? status.username
                    ? t("Your chat is linked as @{{username}}.", { username: status.username })
                    : t("Your chat is linked.")
                  : t("Your chat is not linked yet.")}
              </span>
            </li>
          </ul>
        ) : null}

        <section aria-labelledby="tg-help-link">
          <h3 id="tg-help-link" className="text-h3 text-text">
            {t("Link your Telegram — every person, once")}
          </h3>
          <ol className="mt-2 list-decimal space-y-1.5 ps-5">
            <li>{t("Install Telegram on your phone or computer and sign in.")}</li>
            <li>
              {t(
                "On the Telegram settings page press “Connect Telegram”. Docit makes a link that works once, for 15 minutes.",
              )}
            </li>
            <li>
              {t(
                "Open the link — Telegram opens a chat with the bot — and press Start. Do it in your own chat with the bot, not in a group.",
              )}
            </li>
            <li>
              {t(
                "Within a few seconds the settings page says “Connected as @you”. From then on, questions and mail alerts arrive in that chat.",
              )}
            </li>
          </ol>
          <p className="mt-2 text-small text-text-secondary">
            {t(
              "In the chat: tap a button to answer, or reply to the card itself — a choice by its number or name, an approval with yes or no. Send /inbox to see what is waiting, /help for the commands; /stop disconnects every workspace.",
            )}
          </p>
          <p className="mt-1 text-small text-text-tertiary">
            {t(
              "The link binds the chat that presses Start, so nobody can connect your chat by typing an id.",
            )}
          </p>
        </section>

        <section aria-labelledby="tg-help-bot">
          <h3 id="tg-help-bot" className="text-h3 text-text">
            {t("Set up the bot — administrator, once per server")}
          </h3>
          <ol className="mt-2 list-decimal space-y-1.5 ps-5">
            <li>
              {t(
                "In Telegram, open @BotFather — the official one, with the blue check mark — and send /newbot.",
              )}
            </li>
            <li>
              {t(
                "Give the bot a name people will see (for example “Docit”) and a username that ends in “bot” (for example docit_desk_bot).",
              )}
            </li>
            <li>
              {t(
                "BotFather answers with the token — one line that looks like 1234567890:AAH… Treat it as a password: never paste it in a chat, a ticket or a screenshot.",
              )}
            </li>
            <li>
              {t("On the server, put it in the .env file next to docker-compose.production.yml:")}
              <pre className="mt-1 overflow-x-auto rounded-md bg-bg-tertiary px-2 py-1 font-mono text-small">
                TELEGRAM_BOT_TOKEN=1234567890:AAH…
              </pre>
              {t("then redeploy the API and the worker:")}
              <pre className="mt-1 overflow-x-auto rounded-md bg-bg-tertiary px-2 py-1 font-mono text-small">
                {"./deploy-all.sh --only knowit-web-api\n./deploy-all.sh --only knowit-worker"}
              </pre>
            </li>
            <li>
              {t(
                "At start the API registers the bot's webhook on this site's address (Work:TelegramWebhookBaseUrl). Check it with:",
              )}
              <pre className="mt-1 overflow-x-auto rounded-md bg-bg-tertiary px-2 py-1 font-mono text-small">
                docker logs knowit-web-api | grep -i telegram
              </pre>
              {t("The Telegram settings page then shows “Connect Telegram” to everyone.")}
            </li>
          </ol>
          <p className="mt-2 text-small text-text-secondary">
            {t(
              "Optional, in BotFather: /setdescription and /setuserpic. If the token ever leaks, /revoke in BotFather makes a new one — update .env and redeploy.",
            )}
          </p>
          <p className="mt-1 text-small text-text-tertiary">
            {t("Private chats only: the bot does not work in groups, by design.")}
          </p>
        </section>
      </div>
    </Dialog>
  );
}
